import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import {
  IAdvanceSearchSocialMedia,
  IDateRange,
  ILabel,
  TCollabRole,
  TContentType,
  TCreatorType,
  TPostMadeType,
  TPostOwnership,
  TSearchScope,
  TSentiment,
  TValidation
} from 'src/models/general';
import defaultDateRange from 'src/utils/defaultDateRange';

export const initialState: IAdvanceSearchSocialMedia = {
  sentiment: [],
  postOwnership: [],
  contentTypeList: [],
  validationList: [],
  labelList: [],
  excludeLabelList: [],
  includeKeywords: [],
  excludeKeywords: [],
  fromUsername: '',
  replyToOriginalId: [],
  showEmptyLabel: false,
  sentimentConfidenceLevelMin: 0,
  sentimentConfidenceLevelMax: 1,
  streamsCommentingToPost: {
    result: [],
    totalRows: 0,
    totalPages: 0,
    page: 1,
    limit: 20
  },
  selectedOriginalToPost: [],
  queryStringBooleanOperator: 'and',
  languageList: [],
  userRating: [],
  emotion: [],
  validationEmotionList: [],
  selectAllPrimaryPost: false, // for commenting to post
  retweetFilter: 'all',
  talkType: [],
  ageGeneration: [],
  postMadeType: 'postmade',
  commentingToPostDateRange: {
    start: moment(defaultDateRange().startDate).toDate(),
    end: moment(defaultDateRange().endDate).toDate()
  },
  commentingToPostLabelList: [],
  searchScope: 'content',
  collabRole: undefined,
  creatorType: undefined,
  isCollab: undefined
};

export const advanceSearchSocialMedia = createSlice({
  name: 'advanceSearchSocialMedia',
  initialState,
  reducers: {
    reducerUpdateAdvanceSearchSentiment: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<Array<TSentiment>>
    ) => {
      state.sentiment = action.payload;
    },
    reducerUpdateAdvanceSearchPostOwnership: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<Array<TPostOwnership>>
    ) => {
      state.postOwnership = action.payload;
    },
    reducerUpdateAdvanceSearchContentTypeList: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<Array<TContentType>>
    ) => {
      state.contentTypeList = action.payload;
    },
    reducerUpdateAdvanceSearchValidationList: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<Array<TValidation>>
    ) => {
      state.validationList = action.payload;
    },
    reducerUpdateAdvanceSearchLabelList: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<Array<string>>
    ) => {
      state.labelList = action.payload;
    },
    reducerUpdateAdvanceSearchIncludeKeywords: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<Array<string>>
    ) => {
      state.includeKeywords = action.payload;
    },
    reducerUpdateAdvanceSearchExcludeKeywords: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<Array<string>>
    ) => {
      state.excludeKeywords = action.payload;
    },
    reducerUpdateAdvanceSearchFromUsername: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<string>
    ) => {
      state.fromUsername = action.payload;
    },
    reducerUpdateAdvanceSearchReplyToOriginalId: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<Array<string>>
    ) => {
      state.replyToOriginalId = action.payload;
    },
    reducerUpdateAdvanceSearchShowEmptyLabel: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<boolean>
    ) => {
      state.showEmptyLabel = action.payload;
    },
    reducerUpdateAdvanceSearchSentimentConfidenceLevelMin: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<number>
    ) => {
      state.sentimentConfidenceLevelMin = action.payload;
    },
    reducerUpdateAdvanceSearchSentimentConfidenceLevelMax: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<number>
    ) => {
      state.sentimentConfidenceLevelMax = action.payload;
    },
    reducerUpdateSelectAllPrimaryPost: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<boolean>
    ) => {
      state.selectAllPrimaryPost = action.payload;
    },
    reducerUpdatePostMadeType: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<TPostMadeType>
    ) => {
      state.postMadeType = action.payload;
    },
    reducerUpdateCommentingToPostDateRange: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<IDateRange>
    ) => {
      state.commentingToPostDateRange = action.payload;
    },
    reducerUpdateCommentingToPostLabelList: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<Array<ILabel>>
    ) => {
      state.commentingToPostLabelList = action.payload;
    },
    reducerUpdateSearchScope: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<TSearchScope>
    ) => {
      state.searchScope = action.payload;
    },
    reducerUpdateAdvanceSearchCollapseRole: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<TCollabRole | undefined>
    ) => {
      state.collabRole = action.payload;
    },
    reducerUpdateAdvanceSearchCreatorType: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<TCreatorType | undefined>
    ) => {
      state.creatorType = action.payload;
    },
    reducerUpdateAdvanceSearchIsCollab: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<boolean | undefined>
    ) => {
      state.isCollab = action.payload;
    },
    reducerUpdateAdvanceSearch: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<IAdvanceSearchSocialMedia>
    ) => {
      state.includeKeywords = action.payload.includeKeywords;
      state.excludeKeywords = action.payload.excludeKeywords;
      state.fromUsername = action.payload.fromUsername;
      state.sentiment = action.payload.sentiment;
      state.postOwnership = action.payload.postOwnership;
      state.validationList = action.payload.validationList;
      state.contentTypeList = action.payload.contentTypeList;
      state.labelList = action.payload.labelList;
      state.excludeLabelList = action.payload.excludeLabelList;
      state.sentimentConfidenceLevelMin =
        action.payload.sentimentConfidenceLevelMin;
      state.sentimentConfidenceLevelMax =
        action.payload.sentimentConfidenceLevelMax;
      state.showEmptyLabel = action.payload.showEmptyLabel;
      state.replyToOriginalId = action.payload.replyToOriginalId;
      state.streamsCommentingToPost = action.payload.streamsCommentingToPost;
      state.selectedOriginalToPost = action.payload.selectedOriginalToPost;
      state.searchOriginalToPost = action.payload.searchOriginalToPost;
      state.queryStringBooleanOperator =
        action.payload.queryStringBooleanOperator;
      state.languageList = action.payload.languageList;
      state.userRating = action.payload.userRating;
      state.emotion = action.payload.emotion;
      state.validationEmotionList = action.payload.validationEmotionList;
      state.selectAllPrimaryPost = action.payload.selectAllPrimaryPost;
      state.retweetFilter = action.payload.retweetFilter;
      state.talkType = action.payload.talkType;
      state.ageGeneration = action.payload.ageGeneration;
      state.postMadeType = action.payload.postMadeType;
      state.searchScope = action.payload.searchScope;
    },
    reducerUpdateAdvanceClear: (
      state: IAdvanceSearchSocialMedia,
      action: PayloadAction<IAdvanceSearchSocialMedia>
    ) => {
      state.includeKeywords = action.payload.includeKeywords;
      state.excludeKeywords = action.payload.excludeKeywords;
      state.fromUsername = action.payload.fromUsername;
      state.sentiment = action.payload.sentiment;
      state.postOwnership = action.payload.postOwnership;
      state.validationList = action.payload.validationList;
      state.contentTypeList = action.payload.contentTypeList;
      state.labelList = action.payload.labelList;
      state.excludeLabelList = action.payload.excludeLabelList;
      state.sentimentConfidenceLevelMin =
        action.payload.sentimentConfidenceLevelMin;
      state.sentimentConfidenceLevelMax =
        action.payload.sentimentConfidenceLevelMax;
      state.showEmptyLabel = action.payload.showEmptyLabel;
      state.replyToOriginalId = action.payload.replyToOriginalId;
      state.streamsCommentingToPost = action.payload.streamsCommentingToPost;
      state.selectedOriginalToPost = action.payload.selectedOriginalToPost;
      state.searchOriginalToPost = action.payload.searchOriginalToPost;
      state.queryStringBooleanOperator =
        action.payload.queryStringBooleanOperator;
      state.languageList = action.payload.languageList;
      state.userRating = action.payload.userRating;
      state.emotion = action.payload.emotion;
      state.validationEmotionList = action.payload.validationEmotionList;
      state.retweetFilter = action.payload.retweetFilter;
      state.talkType = action.payload.talkType;
      state.ageGeneration = action.payload.ageGeneration;
      state.postMadeType = action.payload.postMadeType;
      state.selectAllPrimaryPost = action.payload.selectAllPrimaryPost;
      state.commentingToPostLabelList =
        action.payload.commentingToPostLabelList;
      state.searchScope = action.payload.searchScope;
    }
  }
});

export const {
  reducerUpdateAdvanceSearch,
  reducerUpdateAdvanceSearchSentiment,
  reducerUpdateAdvanceSearchContentTypeList,
  reducerUpdateAdvanceSearchExcludeKeywords,
  reducerUpdateAdvanceSearchFromUsername,
  reducerUpdateAdvanceSearchIncludeKeywords,
  reducerUpdateAdvanceSearchLabelList,
  reducerUpdateAdvanceSearchPostOwnership,
  reducerUpdateAdvanceSearchReplyToOriginalId,
  reducerUpdateAdvanceSearchSentimentConfidenceLevelMax,
  reducerUpdateAdvanceSearchSentimentConfidenceLevelMin,
  reducerUpdateAdvanceSearchShowEmptyLabel,
  reducerUpdateAdvanceSearchValidationList,
  reducerUpdateAdvanceClear,
  reducerUpdateSelectAllPrimaryPost,
  reducerUpdatePostMadeType,
  reducerUpdateCommentingToPostDateRange,
  reducerUpdateCommentingToPostLabelList,
  reducerUpdateSearchScope,
  reducerUpdateAdvanceSearchCollapseRole,
  reducerUpdateAdvanceSearchCreatorType,
  reducerUpdateAdvanceSearchIsCollab
} = advanceSearchSocialMedia.actions;

export default advanceSearchSocialMedia.reducer;
